import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
  useRef,
  ReactNode,
} from 'react';
import { Button, Col, Layout, message, Row, Space, Spin, Typography } from 'antd';
import moment, { Moment } from 'moment';
import {  isEmpty } from 'underscore';
import { ChartBox } from '../ChartBox';
import { IPlotVariable } from '../../../../typings/analytics';
import { DateFormat, DT0Format } from '../../../../generic/StaticVariable';
import { Select, DatePicker } from 'antd';
import { Label } from '../../../../components/Label/Label';
import styles from '../../Style/LayoutPanel.module.scss';
import { monthOptions } from './MonthOptions';
import ScatterChart from '../../../../components/Chart/ScatterChart';
import { IBestDataset } from '../../../../typings/analytics';
import { IEnrichedMetadata } from '../../../../typings/metadata';
import {
  IGetBasisChartV2ApiPayload,
  IGetJointScatterPlotV2ApiPayload,
} from '../../../../typings/chart';
import { formatDate } from '../../../../generic/common';
import { StoreContext } from '../../../../store/store';
import { getPointNameAndAcronym } from '../AnalyticsPanel';
import { SplitCoordinates } from '../../../../generic/coordinate';
import { IAnalytcisUserInput } from '../../../../typings/IAnalyticsUserInput';
import Api from '../../../../api/Api';
import IconPoint from '../../../../assets/icon/locate2.png'
import CustomDivider from '../CustomDIvider';
import { MarkerItem } from '../../../../typings/home';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

export default function ScatterPlot(props: {
  coordinates: number[];
  previewDatasetId: string;
  previewDatasetTimeRange: [moment.Moment, moment.Moment];
  categoryDataset: IBestDataset[];
  onDatasetChange: (item: string) => void
  defaultValueShortName: string;
  defaultDirectionShortName: string;
  variables: IPlotVariable[];
  dateRange: string[];
  tsData: { [key: string]: any }[];
  onUpdateScatterPlot: (values: IGetBasisChartV2ApiPayload) => void;
  onUpdateJointScatterPlot: (values: IGetJointScatterPlotV2ApiPayload) => void;
  updatedScatterPlotData: any[];
  pointProps: string;
  pointName: string;
  userInput: IAnalytcisUserInput;
  gridCellSize: string,
  waterDepth: string,
  updating: boolean
  error: string,
  listNavigation: ReactNode
  updateUserInput: React.Dispatch<Partial<IAnalytcisUserInput>>;
  point?: MarkerItem
  onChangeActiveKey: (item: any) => void
  pointOption: { label: string, value: string }[]
}) {


  const {
    datasetBriefs,
    fwePoints,
    setEnrichedMetadata,
    enrichedMetadata
  } = useContext(StoreContext);

  const {
    tsData,
    defaultValueShortName,
    defaultDirectionShortName,
    onUpdateScatterPlot,
    onUpdateJointScatterPlot,
    dateRange,
    variables,
    updatedScatterPlotData,
    previewDatasetId,
    categoryDataset,
    onDatasetChange,
    pointProps,
    pointName,
    userInput,
    updateUserInput,
    gridCellSize,
    waterDepth,
    coordinates,
    previewDatasetTimeRange,
    updating,
    error,
    listNavigation,
    point
  } = props;

  const [datasetX, setDatasetX] = useState(userInput.ScatterPlot.datasetX);
  const [datasetY, setDatasetY] = useState(userInput.ScatterPlot.datasetY);
  const [variableX, setVariableX] = useState<IPlotVariable | null>(
    userInput.ScatterPlot.variableX
  );
  const [variableY, setVariableY] = useState<IPlotVariable | null>(
    userInput.ScatterPlot.variableY
  );
  const [variablesX, setVariablesX] = useState<IPlotVariable[]>([]);
  const [variablesY, setVariablesY] = useState<IPlotVariable[]>([]);

  const [month, setMonth] = useState(userInput.ScatterPlot.month);
  const [seriesX, setSeriesX] = useState({ name: '', max: 0 });
  const [seriesY, setSeriesY] = useState({ name: '', max: 0 });
  const [plotData, setPlotData] = useState<{ x: number; y: number }[]>([]);
  const [readyToUpdate, setReadyToUpdate] = useState(false);
  const [startDate, setStartDate] = useState(
    userInput.ScatterPlot.startDate
      ? userInput.ScatterPlot.startDate
      : moment(dateRange[1]).subtract(1, 'years')
  );
  const [endDate, setEndDate] = useState(
    userInput.ScatterPlot.endDate
      ? userInput.ScatterPlot.endDate
      : moment(dateRange[1])
  );
  const [previewsettingChanged, setPreviewsettingChanged] = useState(false);

  const chartRef = useRef<ScatterChart>(null);

  const handleExportImage = useCallback(() => {
    if (chartRef.current) {
      chartRef.current.exportChartAsImage();
    }
  }, []);

  useEffect(() => {
    updateUserInput({
      ScatterPlot: {
        datasetX: datasetX,
        datasetY: datasetY,
        variableX: variableX,
        variableY: variableY,
        month: month,
        startDate: startDate,
        endDate: endDate,
      },
    });
  }, [datasetX, datasetY, variableX, variableY, month, startDate, endDate]);

  const doStartDateChange = (value: any) => {
    if (value) {
      setPreviewsettingChanged(true);
      setStartDate(value);
    }
  };

  const doEndDateChange = (value: any) => {
    if (value) {
      setPreviewsettingChanged(true);
      setEndDate(value);
    }
  };

  const doMonthChange = (active: number) => {
    setPreviewsettingChanged(true);
    setMonth(active);
  };

  const doDatasetXChange = (active: string) => {
    onDatasetChange(active)
  };

  const doDatasetYChange = async (active: string) => {
    if (!enrichedMetadata) return;

    let metadata = enrichedMetadata.find(
      (md: IEnrichedMetadata) =>
        md.DatasetId === active &&
        md.CoordinatesString === coordinates.join()
    );

    if (!metadata) {
      try {
        setReadyToUpdate(false)
        metadata = await Api.metadata.getMetadata(active);
        const createFullExtractionTasks = await Api.service.createFullExtractionTasks(
          [metadata!],
          coordinates,
          pointName,
          gridCellSize,
          waterDepth,
          datasetBriefs,
          fwePoints,
        );
        const addEnrichedMetadata = enrichedMetadata.concat(createFullExtractionTasks)
        setEnrichedMetadata(addEnrichedMetadata);
      } finally {
        setReadyToUpdate(true)
      }
    }

    if(!metadata) return

    const withoutTime = metadata.ItemsInfo.filter(
      (item: IPlotVariable) => item.Name !== 'Time'
    );
    setPreviewsettingChanged(true);
    setVariablesY(withoutTime);
    setVariableY(withoutTime.length > 0 ? withoutTime[0] : null);
    setDatasetY(active);
  };


  const doVariableChange = (active: number, xy: 'X' | 'Y') => {
    if (xy === 'X' && variablesX.length > 0) {
      const item = variablesX.find((a) => a.Index === active)!;
      setPreviewsettingChanged(true);
      setVariableX(item);
    }

    if (xy === 'Y' && variablesY.length > 0) {
      const item = variablesY.find((a) => a.Index === active)!;
      setPreviewsettingChanged(true);
      setVariableY(item);
    }
  };

  const [downloadName, setDownloadName] = useState<string | undefined>();

  useEffect(() => {
    if (!previewsettingChanged && dateRange && dateRange.length > 1) {
      setDownloadName(
        getPointNameAndAcronym(previewDatasetId, datasetBriefs, point) +
        pointProps +
        '_' +
        formatDate(moment(dateRange[0])) +
        '_' +
        formatDate(moment(dateRange[1])) +
        '_Scatter_Plot'
      );
    }
  }, [
    dateRange,
    previewsettingChanged,
    pointProps,
    previewDatasetId,
    datasetBriefs,
    point && point.label,
  ]);

  useEffect(() => {
    if ((!datasetX || !datasetY) && !isEmpty(categoryDataset)) {
      setDatasetX(previewDatasetId);
      setDatasetY(previewDatasetId);
    }
  }, [datasetX, datasetY, categoryDataset, previewDatasetId]);

  useEffect(() => {
    const ready = enrichedMetadata ? enrichedMetadata.length > 0 : false;
    setReadyToUpdate(ready);
  }, [enrichedMetadata]);

  useEffect(() => {
    if (!isEmpty(variables)) {
      const variableList = variables.filter((item) => item.Name !== 'Time');

      setVariablesX(variableList);
      setVariablesY(variableList);
      if (
        (!variableX || !variableY) &&
        defaultValueShortName &&
        defaultDirectionShortName &&
        variableList.length > 0 &&
        variableList.length > 1
      ) {
        const defaultVariableX = variableList.find(
          (v: IPlotVariable) => v.ShortName === defaultValueShortName
        );
        defaultVariableX !== undefined
          ? setVariableX(defaultVariableX)
          : setVariableX(variableList[0]);
        const defaultVariableY = variableList.find(
          (v: IPlotVariable) => v.ShortName === defaultDirectionShortName
        );
        defaultVariableY !== undefined
          ? setVariableY(defaultVariableY)
          : setVariableY(variableList[1]);
      }
    }
  }, [variables, defaultValueShortName, defaultDirectionShortName]);

  useEffect(() => {
    previewsettingChanged &&
      message.warn('Please click Update once you finished your selection');
  }, [previewsettingChanged]);

  useEffect(() => {
    if (
      !previewsettingChanged &&
      variableX !== undefined &&
      variableY !== undefined
    ) {
      const dataKeyX = variableX?.Name.split(' ').join('_') || '';
      const dataKeyY = variableY?.Name.split(' ').join('_') || '';

      const firstTSData = tsData && tsData[0] ? tsData[0] : null;
      if (
        variableX &&
        variableY &&
        firstTSData &&
        dataKeyX in firstTSData &&
        dataKeyY in firstTSData
      ) {
        drawTable(tsData, dataKeyX, dataKeyY, variableX, variableY);
      }
    }
  }, [variableX, variableY, tsData, previewsettingChanged]);

  function drawTable(
    data: any[],
    dataKeyX: string,
    dataKeyY: string,
    variableX: IPlotVariable,
    variableY: IPlotVariable
  ) {
    const filterData: { [key: number]: number[] } = {};
    const xValues = data.map(x => parseFloat(x[dataKeyX]));
    const yValues = data.map(x => parseFloat(x[dataKeyY]));
    let xMax = 0;
    let yMax = 0;
    for (let index = 0; index < data.length; index++) {
      const x = xValues[index];
      const y = yValues[index];
      yMax = y > yMax ? y : yMax;
      xMax = x > xMax ? x : xMax;
      if (filterData[x] === undefined) {
        filterData[x] = [y];
      } else if (!filterData[x].includes(y)) {
        filterData[x].push(y);
      }
    }

    const newPlotData = Object.keys(filterData).map((k) => {
      const emptyData: { [key: string]: number } = {};
      const numK = parseFloat(k);
      const filterDataForNumK = filterData[numK];
      if (filterDataForNumK === undefined) {
        return emptyData;
      } else {
        const data = filterDataForNumK.reduce(
          (a, b, i) => {
            return { ...a, [`y${i}`]: b };
          },
          { x: numK }
        );
        return data;
      }
    });

    setSeriesX({
      name: `${variableX.Name} [[${variableX.Unit}]]`,
      max: xMax * 1.2,
    });
    setSeriesY({
      name: `${variableY.Name} [[${variableY.Unit}]]`,
      max: yMax * 1.2,
    });

    setTimeout(() => {
      setPlotData(
        newPlotData === null
          ? newPlotData
          : (newPlotData.sort((a, b) => a.x - b.x) as any)
      );
    }, 500);
  }

  useEffect(() => {
    if (
      variableX !== undefined &&
      variableY !== undefined &&
      updatedScatterPlotData.length > 0
    ) {
      let dataKeyX = variableX?.Name.split(' ').join('_') || '';
      let dataKeyY = variableY?.Name.split(' ').join('_') || '';

      if (dataKeyX === dataKeyY && datasetX !== datasetY){
        dataKeyX = `${dataKeyX}_X`;
        dataKeyY = `${dataKeyY}_Y`;
      }

      const firstTSData =
        updatedScatterPlotData && updatedScatterPlotData[0]
          ? updatedScatterPlotData[0]
          : null;

      if (
        variableX &&
        variableY &&
        firstTSData &&
        dataKeyX in firstTSData &&
        dataKeyY in firstTSData
      ) {
        drawTable(
          updatedScatterPlotData,
          dataKeyX,
          dataKeyY,
          variableX,
          variableY
        );
      }
    }
  }, [variableX, variableY, updatedScatterPlotData]);

  const onUpdateScatterPlotData = useCallback(() => {
    if (datasetX && datasetY && variableX && variableY) {
      if (datasetX === datasetY) {
        setDownloadName(
          getPointNameAndAcronym(datasetX, datasetBriefs, point) +
          pointProps +
          '_' +
          formatDate(startDate) +
          '_' +
          formatDate(endDate) +
          '_Scatter_Plot'
        );
        const values: IGetBasisChartV2ApiPayload = {
          datasetName: datasetX,
          parameters: variableX.Name + '|' + variableY.Name,
          startDate: startDate.format(DT0Format),
          endDate: endDate.format(DT0Format),
          month: month,
        };
        onUpdateScatterPlot(values);
      } else {
        setDownloadName(
          getPointNameAndAcronym(datasetX, datasetBriefs, point, datasetY) +
          pointProps +
          '_' +
          formatDate(startDate) +
          '_' +
          formatDate(endDate) +
          '_Scatter_Plot'
        );
        const values: IGetJointScatterPlotV2ApiPayload = {
          datasetName: datasetX,
          datasetName2: datasetY,
          parameters: variableX.Name,
          parameters2: variableY.Name,
          startDate: startDate.format(DT0Format),
          endDate: endDate.format(DT0Format),
          month: month,
        };
        onUpdateJointScatterPlot(values);
      }
    }
  }, [
    startDate,
    endDate,
    datasetX,
    datasetY,
    variableX,
    variableY,
    month,
    onUpdateScatterPlot,
    onUpdateJointScatterPlot,
  ]);

  function periodGreater10years() {
    const years = endDate.diff(startDate, 'years');
    return years >= 10;
  }

  const dataPreformanceWarning = useMemo(() => {
    return periodGreater10years();
  }, [startDate, endDate]);

  function disabledStartDate(current: Moment) {
    // Can not select date after end date
    const isAfterEndDate = current && current > endDate;
    // and not before preview dataset's time range and not after preview dataset's time range
    return isAfterEndDate
      ? isAfterEndDate
      : current.isBefore(previewDatasetTimeRange[0], 'day');
  }

  function disabledEndDate(current: Moment) {
    // Can not select date before start date and not after preview dataset's time range
    const isBeforeStartDate = current && current < startDate;
    return isBeforeStartDate
      ? isBeforeStartDate
      : current.isAfter(previewDatasetTimeRange[1], 'day');
  }

  const memoCoords = useMemo(() => {
    return SplitCoordinates(pointProps);
  }, [pointProps]);

  const categories = useMemo(() => {
    const label = categoryDataset.map((bd: IBestDataset) => {
      return bd.label;
    })

    return Array.from(new Set(label));
  }, [categoryDataset])

  return (
    <Layout style={{minWidth: 1200, height: '100%', overflow: 'hidden', background: 'white'}}>
      <Header className={styles.header}>
        <Row justify="space-between" align="middle" className={styles.headerRow}>
          <Col>
            <Row gutter={[0, 8]} align="bottom">
              <Col className={styles.colPoint}>
              <Space>
                <Text>Point</Text>
                <img src={IconPoint} width={20} height={20} />
              </Space>
              <Select
                style={{ width: '100%', marginTop: '4px' }}
                placeholder="Select point"
                onChange={props.onChangeActiveKey}
                options={props.pointOption}
                value={point}
              />
              </Col>
              <Col span={24} md={6} className={styles.colItem}>
                <Text>Dataset</Text>
                <br />
                <Select
                  value={datasetX}
                  style={{ width: '100%', marginTop: '4px', minWidth: 220 }}
                  onChange={doDatasetXChange}
                  disabled={!readyToUpdate}
                >
                  {categories.map((category: string) => {
                    const section = category
                    return (
                      <Select.OptGroup key={category} label={section}>
                        {categoryDataset
                          .filter((item) => {
                            return item.label === category;
                          })
                          .map((item) => {
                            return (
                              <Select.Option key={item.id} value={item.id}>
                                {item.value}
                              </Select.Option>
                            );
                          })}
                      </Select.OptGroup>
                    );
                  })}
                  </Select>
              </Col>
              <Col span={24} md={6} className={styles.colItem}>
                <Text>Variable</Text>
                <br />
                <Select
                  style={{ width: '100%', marginTop: '4px', minWidth: 220 }}
                  placeholder="Select variable"
                  onChange={(active) => {
                    doVariableChange(active, 'X');
                  }}
                  value={variableX?.Index ? variableX.Index : 0}
                  disabled={updating}
                >
                  {variablesX
                  .filter((item) => item.Name !== 'Time')
                  .map((item) => {
                    return (
                      <Select.Option key={item.Index} value={item.Index}>
                        {item.Name}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Col>
              <Col span={24} md={3} className={styles.colItem}>
                <Text>Start Date</Text>
                <DatePicker
                  value={moment(startDate)}
                  disabledDate={disabledStartDate}
                  onChange={doStartDateChange}
                  format={DateFormat}
                  disabled={updating}
                />
              </Col>
              <Col span={24} md={3} className={styles.colItem}>
                <Text>End Date</Text>
                <DatePicker
                  value={moment(endDate)}
                  disabledDate={disabledEndDate}
                  onChange={doEndDateChange}
                  format={DateFormat}
                  disabled={updating}
                />
              </Col>
            </Row>
          </Col>
          <Col style={{width: 201}}>
          <div style={{height: '60px', padding: 20, borderLeft: '2px solid #f0f0f0'}}>
            <Button
              type="primary"
              onClick={onUpdateScatterPlotData}
              loading={updating}
              disabled={!setReadyToUpdate}
              style={{ width: 100, bottom: 10}}
            >
              Update
            </Button>
          </div>
          </Col>
        </Row>
      </Header>
      <CustomDivider  />
      <Layout>
        <Sider theme="light">
          {listNavigation}
        </Sider>
        <Content style={{ padding: '10px', minHeight: '400px', borderLeft: '2px solid #f0f0f0', borderRight: '2px solid #f0f0f0', background: 'white'}}>
         {!updating && error && <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>{error}</div>}
         {!updating && !error && (
          <div className={styles.chartPointWrapper}>
            <div className={styles.analyticsPointName}>
              {pointName}
              <span className={styles.analyticsCoords}>
                ({memoCoords.lng}°E; {memoCoords.lat}°N)
              </span>
            </div>
            <ChartBox
              actionRow={<></>}
              key={'Scatter Plot'}
              title={'Scatter Plot'}
              height={320}
              plot={
                !error ?
                  <ScatterChart
                    ref={chartRef}
                    seriesX={seriesX}
                    seriesY={seriesY}
                    data={plotData}
                    downloadName={downloadName}
                  />
                :
                  <>{error}</>
              }
              dataset={categoryDataset.find((d) => d.id === datasetX)?.label || ''}
              customLink={
                <Button type='link' className={styles.buttonLink} onClick={handleExportImage} loading={updating}>
                  Download as JPG
                </Button>
              }
            />
          </div>
          )}
          {updating && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>
              <Spin style={{margin: 'auto'}} size='large' />
            </div>
          )}
        </Content>
        <Sider
          width={200}
          className={styles.siderChildren}
          theme="light"
          style={{
            display: 'flex',
            flexDirection: 'column',
            padding: 5,
          }}
        >
          <Label label="DatasetY" className={styles.actionItem}>
            <Select
              value={datasetY}
              size="small"
              style={{ width: '100%' }}
              onChange={doDatasetYChange}
              disabled={!readyToUpdate}
            >
              {categories.map((category: string) => {
                const section = category
                return (
                  <Select.OptGroup key={category} label={section}>
                    {categoryDataset
                      .filter((item) => {
                        return item.label === category;
                      })
                      .map((item) => {
                        return (
                          <Select.Option key={item.id} value={item.id}>
                            {item.value}
                          </Select.Option>
                        );
                      })}
                  </Select.OptGroup>
                );
              })}
            </Select>
          </Label>
          <Label label="VariableY" className={styles.actionItem}>
            <Select
              value={variableY?.Index ? variableY.Index : 0}
              size="small"
              style={{ width: '100%' }}
              onChange={(active) => {
                doVariableChange(active, 'Y');
              }}
            >
              {variablesY
                .filter((item) => item.Name !== 'Time')
                .map((item) => {
                  return (
                    <Select.Option key={item.Index} value={item.Index}>
                      {item.Name}
                    </Select.Option>
                  );
                })}
            </Select>
          </Label>
          <Label label="Month" className={styles.actionItem}>
            <Select
              value={month}
              style={{ width: '100%' }}
              onChange={doMonthChange}
              options={monthOptions}
            />
          </Label>
          <div style={{ textAlign: 'center', marginTop: 'auto' }}>
          {dataPreformanceWarning && (
            <div className={styles.dataPreformanceWarning}>
              <div>
                Over 100,000 data points (10 years of 1-hourly data) may affect
                performance.
              </div>
            </div>
          )}
          </div>
        </Sider>
      </Layout>
    </Layout>
  );
}
